import type { Libraries } from '@react-google-maps/api'

/**
 * Shared Google Maps JS API loader options for the Builder app.
 *
 * IMPORTANT: The Google Maps JS API can only be loaded ONCE per page, and
 * `@react-google-maps/api`'s `useJsApiLoader` throws
 * "Loader must not be called again with different options" if two call sites
 * request different options under the same loader id. Several Builder pages use
 * Maps (zone editor, deadhead hub address, airport search), so every call site
 * MUST pass these identical options.
 *
 * NOTE on the `drawing` library:
 * Google REMOVED the `drawing` library (google.maps.drawing.DrawingManager)
 * from the Maps JavaScript API in v3.65 and it is unavailable as of May 2026.
 * Pinning an old version (e.g. v=3.64) does NOT restore it because the library
 * has been retired from all currently-serviceable versions. The ZoneMapEditor
 * therefore implements its own click-to-add-vertex polygon drawing instead of
 * relying on DrawingManager.
 * See: https://developers.google.com/maps/deprecations
 *
 * We load the superset of libraries needed across the Builder so the options
 * object is identical everywhere. `places` powers address/airport autocomplete;
 * `geometry` is used by the zone editor for spherical math / bounds.
 */
export const GOOGLE_MAPS_LIBRARIES: Libraries = ['places', 'geometry']

export const GOOGLE_MAPS_API_KEY: string =
  import.meta.env.VITE_GOOGLE_MAPS_API_KEY || ''

/**
 * Canonical loader options shared by every Builder `useJsApiLoader` call site.
 */
export const GOOGLE_MAPS_LOADER_OPTIONS = {
  id: 'limo-builder-google-maps',
  googleMapsApiKey: GOOGLE_MAPS_API_KEY,
  libraries: GOOGLE_MAPS_LIBRARIES,
} as const
